import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useFormPersistence } from '@/hooks/use-form-persistence';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import UnifiedFileUpload from '@/components/ui/UnifiedFileUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Upload, Plus, PlusCircle, AlertTriangle, CheckCircle, XCircle, File } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

type PractitionerCertification = {
  id: string;
  practitioner_id: string;
  certificate_name: string;
  issuing_organization: string;
  certification_type: string;
  issue_date: string;
  expiry_date: string | null;
  description: string | null;
  file_url: string;
  file_type: string;
  file_name: string;
  status: string;
  created_at: string;
  updated_at: string;
};

const formSchema = z.object({
  certificate_name: z.string().min(1, { message: 'Certificate name is required' }),
  issuing_organization: z.string().min(1, { message: 'Issuing organization is required' }),
  certification_type: z.string().min(1, { message: 'Certification type is required' }),
  issue_date: z.string().min(1, { message: 'Issue date is required' }),
  expiry_date: z.string().optional(),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CertificationManagerProps {
  userId: string;
}

const CertificationManager: React.FC<CertificationManagerProps> = ({ userId }) => {
  const { t } = useTranslation('dashboard');
  const [certifications, setCertifications] = useState<PractitionerCertification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      certificate_name: '',
      issuing_organization: '',
      certification_type: '',
      issue_date: format(new Date(), 'yyyy-MM-dd'),
      expiry_date: '',
      description: '',
    },
  });

  // Add form persistence
  const { markAsSaved, clearStorage } = useFormPersistence({
    key: `certification_${userId}`,
    form,
    exclude: ['selectedFile'], // Don't persist file selections
  });

  useEffect(() => {
    if (userId) {
      fetchCertifications();
    }
  }, [userId]);

  const fetchCertifications = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('practitioner_certifications')
        .select('*')
        .eq('practitioner_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCertifications(data || []);
    } catch (error) {
      console.error('Error fetching certifications:', error);
      toast({
        variant: 'destructive',
        title: t('certificationManagement.messages.failedToLoad'),
        description: t('certificationManagement.messages.failedToLoadDescription'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast({
          variant: 'destructive',
          title: t('certificationManagement.messages.invalidFileType'),
          description: t('certificationManagement.messages.invalidFileTypeDescription'),
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          variant: 'destructive',
          title: t('certificationManagement.messages.fileTooLarge'),
          description: t('certificationManagement.messages.fileTooLargeDescription'),
        });
        return;
      }

      setSelectedFile(file);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;

    const fileExt = selectedFile.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    // Fix: Use user-specific path that matches RLS policy
    const filePath = `${userId}/certifications/${fileName}`;

    try {
      console.log('Uploading file to path:', filePath);
      const { data, error } = await supabase.storage
        .from('practitioner_files')
        .upload(filePath, selectedFile, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }

      console.log('Upload successful:', data);

      // Store and return the private file path (not a public URL)
      return filePath;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (!selectedFile) {
      toast({
        variant: 'destructive',
        title: t('certificationManagement.messages.fileRequired'),
        description: t('certificationManagement.messages.fileRequiredDescription'),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const fileUrl = await uploadFile();
      if (!fileUrl) throw new Error('File upload failed');

      console.log('Inserting certification with file URL:', fileUrl);

      const { error } = await supabase
        .from('practitioner_certifications')
        .insert({
          practitioner_id: userId,
          certificate_name: values.certificate_name,
          issuing_organization: values.issuing_organization,
          certification_type: values.certification_type,
          issue_date: values.issue_date,
          expiry_date: values.expiry_date || null,
          description: values.description || null,
          file_url: fileUrl,
          file_type: selectedFile.type,
          file_name: selectedFile.name,
          status: 'pending_review',
        });

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      toast({
        title: t('certificationManagement.messages.certificationAdded'),
        description: t('certificationManagement.messages.certificationAddedDescription'),
      });

      form.reset();
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsAddingNew(false);
      markAsSaved(); // Mark as saved and clear localStorage
      fetchCertifications();
    } catch (error) {
      console.error('Error saving certification:', error);
      toast({
        variant: 'destructive',
        title: t('certificationManagement.messages.failedToSave'),
        description: t('certificationManagement.messages.failedToSaveDescription'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 flex items-center gap-1 border-green-200">
            <CheckCircle className="h-3 w-3" /> {t('certificationManagement.status.approved')}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-50 text-red-700 flex items-center gap-1 border-red-200">
            <XCircle className="h-3 w-3" /> {t('certificationManagement.status.rejected')}
          </Badge>
        );
      case 'pending_review':
      default:
        return (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 flex items-center gap-1 border-amber-200">
            <AlertTriangle className="h-3 w-3" /> {t('certificationManagement.status.pendingReview')}
          </Badge>
        );
    }
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Open file using a short-lived signed URL (works when bucket is private)
  const openCertificationFile = async (pathOrUrl: string) => {
    try {
      let bucket = 'practitioner_files';
      let path = pathOrUrl;

      // If stored as a public URL, extract bucket and path
      if (pathOrUrl.startsWith('http')) {
        const match = pathOrUrl.match(/\/object\/public\/([^/]+)\/(.+)$/);
        if (match) {
          bucket = match[1];
          path = match[2];
        }
      }

      const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 60);
      const url = error || !data?.signedUrl ? pathOrUrl : data.signedUrl;
      
      // For mobile, open in same tab to avoid popup blockers
      if (window.innerWidth < 768) {
        window.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (e) {
      console.error('Error opening certification file:', e);
      if (window.innerWidth < 768) {
        window.location.href = pathOrUrl;
      } else {
        window.open(pathOrUrl, '_blank');
      }
    }
  };

  return (
    <div className="space-y-6">{/* Removed pb-8 md:pb-6 since this is now a sub-component */}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <>
          {certifications.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {certifications.map((cert) => (
                <Card key={cert.id} className="overflow-hidden">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg font-medium">{cert.certificate_name}</CardTitle>
                        <CardDescription>{cert.issuing_organization}</CardDescription>
                      </div>
                      {getStatusBadge(cert.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">{t('certificationManagement.table.type')}</p>
                        <p className="text-sm">{cert.certification_type}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">{t('certificationManagement.table.issueDate')}</p>
                        <p className="text-sm">{cert.issue_date}</p>
                      </div>
                    </div>
                    {cert.description && (
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">{t('certificationManagement.table.description')}</p>
                        <p className="text-sm">{cert.description}</p>
                      </div>
                    )}
                  </CardContent>
                  <CardFooter className="pt-2 flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      {new Date(cert.created_at).toLocaleDateString()}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openCertificationFile(cert.file_url)}
                    >
                      <File className="h-4 w-4 mr-1" /> {t('certificationManagement.viewFile')}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}

          {!isAddingNew ? (
            <Button
              onClick={() => setIsAddingNew(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <PlusCircle className="h-4 w-4 mr-2" /> {t('certificationManagement.addNewCertification')}
            </Button>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>{t('certificationManagement.addNewCertification')}</CardTitle>
                <CardDescription>
                  {t('certificationManagement.addNewCertificationDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="certificate_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('certificationManagement.form.certificateName')}</FormLabel>
                            <FormControl>
                              <Input placeholder={t('certificationManagement.form.certificateNamePlaceholder')} {...field} disabled={isSubmitting} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="issuing_organization"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('certificationManagement.form.issuingOrganization')}</FormLabel>
                            <FormControl>
                              <Input placeholder={t('certificationManagement.form.issuingOrganizationPlaceholder')} {...field} disabled={isSubmitting} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="certification_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('certificationManagement.form.certificationType')}</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              disabled={isSubmitting}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder={t('certificationManagement.form.selectCertificationType')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="Professional License">{t('certificationManagement.types.professionalLicense')}</SelectItem>
                                <SelectItem value="Certification">{t('certificationManagement.types.certification')}</SelectItem>
                                <SelectItem value="Diploma">{t('certificationManagement.types.diploma')}</SelectItem>
                                <SelectItem value="Degree">{t('certificationManagement.types.degree')}</SelectItem>
                                <SelectItem value="Training Completion">{t('certificationManagement.types.trainingCompletion')}</SelectItem>
                                <SelectItem value="Other">{t('certificationManagement.types.other')}</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="issue_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('certificationManagement.form.issueDate')}</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} disabled={isSubmitting} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="expiry_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('certificationManagement.form.expiryDate')}</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} value={field.value || ''} disabled={isSubmitting} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="md:col-span-2">
                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('certificationManagement.form.description')}</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder={t('certificationManagement.form.descriptionPlaceholder')}
                                  className="min-h-[80px]"
                                  {...field}
                                  value={field.value || ''}
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div>
                      <FormLabel>{t('certificationManagement.form.certificateFile')}</FormLabel>
                      <div className="mt-2">
                        <UnifiedFileUpload
                          accept=".pdf,.jpg,.jpeg,.png"
                          maxFiles={1}
                          maxSize={5}
                          onFilesChange={(files) => {
                            if (files.length > 0) {
                              const file = files[0];
                              const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
                              if (validTypes.includes(file.type)) {
                                setSelectedFile(file);
                                if (file.type.startsWith('image/')) {
                                  setPreviewUrl(URL.createObjectURL(file));
                                } else {
                                  setPreviewUrl(null);
                                }
                              }
                            } else {
                              setSelectedFile(null);
                              setPreviewUrl(null);
                            }
                          }}
                          disabled={isSubmitting}
                          formatHint="Supports PDF, JPG, PNG, JPEG up to 5MB"
                        >
                          Choose Certificate File
                        </UnifiedFileUpload>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setIsAddingNew(false);
                          form.reset();
                          setSelectedFile(null);
                          setPreviewUrl(null);
                        }}
                        disabled={isSubmitting}
                      >
                        {t('certificationManagement.form.cancel')}
                      </Button>
                      <Button 
                        type="submit"
                        disabled={isSubmitting}
                        className="bg-amber-600 hover:bg-amber-700"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {t('certificationManagement.form.uploading')}
                          </>
                        ) : (
                          t('certificationManagement.form.uploadCertification')
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
    </div>
  );
};

export default CertificationManager;
